import React from "react";
import { Link } from "react-router-dom";
import styled, { css } from "styled-components";
import { Theme } from "../theme";
import PropTypes from "prop-types";

const BaseButton = styled.button`
    border-radius: 3px;
    padding: 9px 10px;
    border: none;
    text-decoration: none;
    cursor: pointer;
    text-align: center;
    font-weight: bold;
    font-size: 16px;
    min-height: 44px;

    ${(props) =>
        props.width &&
        css`
            width: ${props.width};
        `}
    ${(props) =>
        props.outline &&
        css`
            border: 1px solid ${props.outline};
        `}
    @media only screen and (min-width: 360px) {
        min-width: 275px;
    }
`;

const PrimaryButton = styled(BaseButton)`
    background-color: ${Theme.colors.blue};
    color: white;

    ${(props) =>
        !props.disabled &&
        css`
            &:hover {
                color: ${Theme.colors.blue};
                background-color: ${Theme.colors.gray[200]};
            }
        `}
    ${(props) =>
        props.disabled &&
        css`
            background-color: ${Theme.colors.gray[400]};
            cursor: default;
            color: ${Theme.colors.gray[700]};
        `}
`;

const SecondaryButton = styled(BaseButton)`
    background-color: white;
    color: ${Theme.colors.blue};
    border: 1px solid ${Theme.colors.blue};

    ${(props) =>
        !props.disabled &&
        css`
            &:hover {
                background-color: ${Theme.colors.gray[200]};
            }
        `}
    ${(props) =>
        props.disabled &&
        css`
            background-color: ${Theme.colors.gray[400]};
            cursor: default;
            color: ${Theme.colors.gray[700]};
            border: none;
        `}
`;

const DangerButton = styled(BaseButton)`
    background-color: ${Theme.colors.red};
    color: white;
    ${(props) =>
        !props.disabled &&
        css`
            &:hover {
                color: ${Theme.colors.red};
                background-color: ${Theme.colors.gray[200]};
            }
        `}
    ${(props) =>
        props.disabled &&
        css`
            background-color: ${Theme.colors.gray[400]};
            cursor: default;
            color: ${Theme.colors.gray[700]};
        `}
`;

const TextButton = styled(BaseButton)`
    appearance: none;
    border: none;
    background: transparent;
    color: ${Theme.colors.blue};
    font-weight: bold;
    cursor: pointer;
    padding: 0.65rem 1.3rem;
    &:hover {
        color: ${Theme.colors.gray[700]};
    }
`;

export const Button = ({ primary, secondary, danger, text, ...props }) => {
    const as = props.href ? "a" : props.to ? Link : "button";
    if (primary) {
        return <PrimaryButton as={as} role="button" {...props} />;
    }
    if (secondary) {
        return <SecondaryButton as={as} role="button" {...props} />;
    }
    if (danger) {
        return <DangerButton as={as} role="button" {...props} />;
    }
    if (text) {
        return <TextButton as={as} role="button" {...props} />;
    }
    return <PrimaryButton as={as} role="button" {...props} />;
};

Button.propTypes = {
    /**
     * Is this the principal call to action on the page?
     */
    primary: PropTypes.bool,
    /**
     * Is this the secondary call to action on the page?
     */
    secondary: PropTypes.bool,
    /**
     * Is this a disabled call to action on the page?
     */

    disabled: PropTypes.bool,
    /**
     * Is this a dangerous call to action on the page?
     */
    danger: PropTypes.bool,
    /**
     * Is this the text only call to action on the page?
     */
    text: PropTypes.bool,
    /**
     * External link if using button as an anchor
     */
    href: PropTypes.string,
    /**
     * Internal link if using button as a ReactRouter Link
     */
    to: PropTypes.string,
    /**
     * Any node to render as a child of the button. Mainly used for the text.
     */
    children: PropTypes.node,
};
